import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { toast } from 'react-toastify';

import {
  useBulkImportUniversalMutation,
  useImportFromSessionMutation,
  useLazyGetImportSessionQuery,
  UniversalBulkImportResponse,
} from 'store/services/import/importApi';
import { ImportSessionStorage, ParsedImportCard } from 'utils/importSessionStorage';
import {
  safeBase64Decode,
  detectFormat,
  parseUniversalCsv,
  calculateImportSummary,
  FormatDetectionResult,
  SupportedFormat,
} from 'utils/universalCsvParser';

import PartnerHeader from './components/PartnerHeader';
import ConditionStep from './components/ConditionStep';
import CardPreview from './components/CardPreview';
import ImportProgress from './components/ImportProgress';
import ImportSuccess from './components/ImportSuccess';
import ImportError from './components/ImportError';

import './UniversalImportPage.css';

type ImportStep = 'loading' | 'condition' | 'preview' | 'importing' | 'success' | 'error';

interface ErrorState {
  title: string;
  message: string;
  details?: string;
}

const UniversalImportPage = () => {
  const { partner } = useParams<{ partner: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const user = useSelector((state: RootState) => state.auth.user);
  const isAuthenticated = !!user;

  const [step, setStep] = useState<ImportStep>('loading');
  const [parsedCards, setParsedCards] = useState<ParsedImportCard[]>([]);
  const [condition, setCondition] = useState('NEAR_MINT');
  const [format, setFormat] = useState<FormatDetectionResult | null>(null);
  const [importResult, setImportResult] = useState<UniversalBulkImportResponse | null>(null);
  const [error, setError] = useState<ErrorState | null>(null);
  const [hasResumedImport, setHasResumedImport] = useState(false);

  // Session-based import state (for large collections optimization)
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [totalCardCount, setTotalCardCount] = useState<number | null>(null);

  const [bulkImport, { isLoading: isBulkImporting }] = useBulkImportUniversalMutation();
  const [importFromSession, { isLoading: isSessionImporting }] = useImportFromSessionMutation();
  const [fetchSession] = useLazyGetImportSessionQuery();

  const isLoading = isBulkImporting || isSessionImporting;

  // Fetch session data from backend (new POST session flow)
  // Only fetches page 1 for preview - full import happens server-side
  const fetchSessionData = useCallback(
    async (sessionId: string) => {
      try {
        // Fetch only page 1 for preview (optimization for large collections)
        const sessionData = await fetchSession({ sessionId, page: 1 }).unwrap();

        if (sessionData.totalCards === 0) {
          setError({
            title: 'No Cards Found',
            message: 'No valid cards were found in your import.',
            details: sessionData.skippedRows
              ? `${sessionData.skippedRows} rows were skipped.`
              : undefined,
          });
          setStep('error');
          return;
        }

        // Map preview cards to ParsedImportCard format (just for display)
        const previewCards: ParsedImportCard[] = sessionData.cards.map((card, index) => ({
          scryfallUuid: card.scryfallUuid,
          cardName: card.cardName,
          setCode: card.setCode,
          quantity: card.quantity,
          foilQuantity: card.foilQuantity,
          language: card.language,
          originalRow: index + 1,
        }));

        // Store sessionId for import (backend will read all cards from session)
        setActiveSessionId(sessionId);
        setTotalCardCount(sessionData.totalCards);

        // Set format based on source
        const sourceFormat = (sessionData.source as SupportedFormat) || 'tradebinder';
        setFormat({
          format: sourceFormat,
          hasScryfallId: true,
          columns: [],
          delimiter: ';',
          totalRows: sessionData.totalCards,
        });

        setParsedCards(previewCards);
        setStep('condition');

        // Show toast for skipped rows / parse warnings (use toastId to prevent duplicates in StrictMode)
        if (sessionData.skippedRows && sessionData.skippedRows > 0) {
          toast.warning(`${sessionData.skippedRows} rows were skipped during parsing.`, {
            toastId: 'import-skipped-rows',
          });
        }
        if (sessionData.parseWarnings && sessionData.parseWarnings.length > 0) {
          toast.warning(`${sessionData.parseWarnings.length} parsing warnings.`, {
            toastId: 'import-parse-warnings',
          });
        }
      } catch (err) {
        // Handle errors (404, expired, network, etc.)
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        const is404 = errorMessage.includes('404') || errorMessage.includes('not found');

        setError({
          title: is404 ? 'Session Not Found' : 'Import Error',
          message: is404
            ? 'This import session has expired or does not exist.'
            : 'Failed to load your import session.',
          details: is404
            ? 'Please try exporting from Card Binder again.'
            : errorMessage,
        });
        setStep('error');
      }
    },
    [fetchSession]
  );

  // Execute import using session endpoint (for session-based flow)
  const executeSessionImport = useCallback(
    async (sessionId: string, selectedCondition: string) => {
      try {
        const result = await importFromSession({
          sessionId,
          condition: selectedCondition,
        }).unwrap();

        setImportResult(result);
        handleImportResult(result);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError({
          title: 'Import Failed',
          message: 'An error occurred while importing your cards.',
          details: errorMessage,
        });
        setStep('error');
      }
    },
    [importFromSession]
  );

  // Execute import using bulk endpoint (for legacy ?data= flow)
  const executeBulkImport = useCallback(
    async (cards: ParsedImportCard[], selectedCondition: string) => {
      try {
        const result = await bulkImport({
          cards: cards.map((c) => ({
            scryfallUuid: c.scryfallUuid,
            cardName: c.cardName,
            setCode: c.setCode,
            quantity: c.quantity,
            foilQuantity: c.foilQuantity,
            condition: selectedCondition,
            language: c.language,
          })),
          source: partner || 'manual',
        }).unwrap();

        setImportResult(result);
        handleImportResult(result);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError({
          title: 'Import Failed',
          message: 'An error occurred while importing your cards.',
          details: errorMessage,
        });
        setStep('error');
      }
    },
    [bulkImport, partner]
  );

  // Handle import result (shared logic)
  const handleImportResult = (result: UniversalBulkImportResponse) => {
    if (result.successCount === 0 && result.errorCount > 0) {
      setError({
        title: 'Import Failed',
        message: 'All cards failed to import.',
        details: result.errors[0]?.error || 'Unknown error',
      });
      setStep('error');
    } else {
      setStep('success');
      // Success page shows import results - no toast needed
    }
  };

  // Decode and parse CSV data on mount (or fetch session data)
  useEffect(() => {
    const sessionId = searchParams.get('session');
    const data = searchParams.get('data');

    // Check for pending import from auth redirect
    const pendingImport = ImportSessionStorage.getPendingImport();

    console.log('[IMPORT] useEffect running', {
      sessionId,
      data: data ? 'present' : 'absent',
      pendingImport: pendingImport ? { sessionId: pendingImport.sessionId, cardCount: pendingImport.cardCount } : null,
      isAuthenticated,
      hasResumedImport,
    });

    if (pendingImport && isAuthenticated && !hasResumedImport) {
      // Resume import from saved state
      console.log('[IMPORT] Resuming from pending import', { sessionId: pendingImport.sessionId });
      setHasResumedImport(true);
      setParsedCards(pendingImport.parsedCards);
      setCondition(pendingImport.selectedCondition);
      setStep('importing');

      if (pendingImport.sessionId) {
        // Session-based import: backend reads all cards from session
        console.log('[IMPORT] Using session-based import', pendingImport.sessionId);
        setActiveSessionId(pendingImport.sessionId);
        executeSessionImport(pendingImport.sessionId, pendingImport.selectedCondition);
      } else {
        // Legacy flow: send parsed cards directly
        console.log('[IMPORT] Using bulk import (no sessionId)');
        executeBulkImport(pendingImport.parsedCards, pendingImport.selectedCondition);
      }

      ImportSessionStorage.clearPendingImport();
      return;
    }

    if (sessionId) {
      // NEW: Session-based flow (TradeBinder POST session)
      fetchSessionData(sessionId);
    } else if (data) {
      // EXISTING: URL data flow (legacy ?data= param)
      try {
        const csvText = safeBase64Decode(data);
        const detectedFormat = detectFormat(csvText);
        const result = parseUniversalCsv(csvText);

        if (result.cards.length === 0) {
          setError({
            title: 'No Cards Found',
            message: 'No valid cards were found in your import.',
            details:
              result.skippedRows > 0
                ? `${result.skippedRows} rows were skipped (deck entries or invalid data).`
                : 'Make sure to export your collection, not just decks.',
          });
          setStep('error');
          return;
        }

        setFormat(detectedFormat);
        setParsedCards(result.cards);
        setStep('condition');

        // Show toast if some rows had errors
        if (result.errors.length > 0) {
          toast.warning(`${result.errors.length} rows had errors and were skipped.`, {
            toastId: 'import-row-errors',
          });
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError({
          title: 'Import Error',
          message: 'Failed to process your import data.',
          details: errorMessage,
        });
        setStep('error');
      }
    } else if (!pendingImport) {
      // No data provided and no pending import
      setError({
        title: 'No Import Data',
        message: 'No import data was provided.',
        details: 'Use a link from a partner app like Card Binder to import your collection.',
      });
      setStep('error');
    }
  }, [searchParams, isAuthenticated, hasResumedImport, executeBulkImport, executeSessionImport, fetchSessionData]);

  const handleImport = async () => {
    if (!isAuthenticated) {
      // Save state and redirect to login
      // For session-based imports, save sessionId so we can resume with the correct import method
      const pendingState = {
        partner: partner || 'unknown',
        parsedCards,
        selectedCondition: condition,
        cardCount: totalCardCount || parsedCards.length,
        sessionId: activeSessionId || undefined,
      };
      console.log('[IMPORT] Saving pending import before login', { sessionId: pendingState.sessionId, cardCount: pendingState.cardCount });
      ImportSessionStorage.savePendingImport(pendingState);
      ImportSessionStorage.setReturnTo(window.location.pathname + window.location.search);
      navigate('/welcome');
      return;
    }

    setStep('importing');

    if (activeSessionId) {
      // Session-based import: backend reads all cards from session
      await executeSessionImport(activeSessionId, condition);
    } else {
      // Legacy flow: send cards directly to bulk import
      await executeBulkImport(parsedCards, condition);
    }
  };

  const handleRetry = () => {
    setError(null);
    setImportResult(null);
    if (parsedCards.length > 0) {
      setStep('condition');
    } else {
      // Re-parse from URL
      const data = searchParams.get('data');
      if (data) {
        try {
          const csvText = safeBase64Decode(data);
          const result = parseUniversalCsv(csvText);
          setParsedCards(result.cards);
          setStep('condition');
        } catch {
          navigate('/');
        }
      } else {
        navigate('/');
      }
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className="universal-import-page">
      <PartnerHeader partner={partner} />

      <div className="universal-import-content">
        {step === 'loading' && (
          <div className="import-loading">
            <div className="import-loading-spinner" />
            <p>Loading your import...</p>
          </div>
        )}

        {step === 'condition' && (
          <ConditionStep
            cardCount={calculateImportSummary(parsedCards).totalCards}
            format={format}
            selectedCondition={condition}
            onConditionChange={setCondition}
            onContinue={() => setStep('preview')}
          />
        )}

        {step === 'preview' && (
          <CardPreview
            cards={parsedCards}
            totalCards={totalCardCount}
            condition={condition}
            onBack={() => setStep('condition')}
            onImport={handleImport}
            isAuthenticated={isAuthenticated}
            isLoading={isLoading}
          />
        )}

        {step === 'importing' && <ImportProgress />}

        {step === 'success' && <ImportSuccess result={importResult} partner={partner} />}

        {step === 'error' && error && (
          <ImportError
            title={error.title}
            message={error.message}
            details={error.details}
            onRetry={handleRetry}
            onBack={handleBack}
          />
        )}
      </div>
    </div>
  );
};

export default UniversalImportPage;
